import { useState, useEffect } from 'react';
import { 
  Settings, 
  Bot, 
  Key, 
  MessageSquare,
  Save,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getSettings, updateSettings } from '../lib/api';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [aiSensyStatus, setAiSensyStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  
  const [form, setForm] = useState({
    bot_name: '',
    personality: 'professional',
    greeting_message: '',
    system_prompt: '',
    aisensy_api_key: '',
    aisensy_project_id: '',
    aisensy_campaign_name: '',
    is_active: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (settings) {
      checkAiSensyStatus();
    }
  }, [settings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await getSettings();
      setSettings(res.data);
      setForm({
        bot_name: res.data.bot_name || '',
        personality: res.data.personality || 'professional',
        greeting_message: res.data.greeting_message || '',
        system_prompt: res.data.system_prompt || '',
        aisensy_api_key: res.data.aisensy_api_key || '',
        aisensy_project_id: res.data.aisensy_project_id || '',
        aisensy_campaign_name: res.data.aisensy_campaign_name || '',
        is_active: res.data.is_active ?? true
      });
    } catch (error) {
      // Production: error logged silently
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const checkAiSensyStatus = async () => {
    try {
      const res = await axios.get(`${API_BASE}/settings/aisensy/status`);
      setAiSensyStatus(res.data);
    } catch (error) {
      // Production: error logged silently
      setAiSensyStatus({ configured: false, connected: false });
    }
  };

  const handleTestConnection = async () => {
    if (!testPhone.trim()) {
      toast.error('Please enter a phone number to test');
      return;
    }
    
    try {
      setTestingConnection(true);
      const res = await axios.post(`${API_BASE}/settings/aisensy/test?phone_number=${encodeURIComponent(testPhone)}`);
      if (res.data.success) {
        toast.success('Test message sent successfully! Check your WhatsApp.');
      } else {
        toast.error(res.data.message || 'Failed to send test message');
      }
    } catch (error) {
      // Production: error logged silently
      toast.error('Failed to test connection');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateSettings(form);
      toast.success('Settings saved successfully');
      await loadSettings();
      // Re-check AiSensy status after saving
      setTimeout(checkAiSensyStatus, 500);
    } catch (error) {
      // Production: error logged silently
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const personalities = [
    { value: 'professional', label: 'Professional', description: 'Formal and business-like' },
    { value: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
    { value: 'technical', label: 'Technical', description: 'Detailed and precise' },
    { value: 'concise', label: 'Concise', description: 'Brief and to the point' }
  ];

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="settings-loading">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 skeleton rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure your WhatsApp bot behavior and integrations
        </p>
      </div>

      <div className="space-y-6">
        {/* Bot Status */}
        <Card className="bg-card/50 border-border/60">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Bot className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Bot Status</CardTitle>
                  <CardDescription>Enable or disable the bot</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                  data-testid="bot-active-switch"
                />
                <Badge className={form.is_active ? "bg-emerald-500/10 text-emerald-500" : ""}>
                  {form.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Bot Identity */}
        <Card className="bg-card/50 border-border/60">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Bot Identity</CardTitle>
                <CardDescription>Customize how the bot presents itself</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Bot Name</label>
              <Input
                value={form.bot_name}
                onChange={(e) => setForm(prev => ({ ...prev, bot_name: e.target.value }))}
                placeholder="e.g., Monk Algo Bot Support"
                data-testid="bot-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Personality</label>
              <Select
                value={form.personality}
                onValueChange={(value) => setForm(prev => ({ ...prev, personality: value }))}
              >
                <SelectTrigger data-testid="personality-select">
                  <SelectValue placeholder="Select personality" />
                </SelectTrigger>
                <SelectContent>
                  {personalities.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <div>
                        <p className="font-medium">{p.label}</p>
                        <p className="text-xs text-muted-foreground">{p.description}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Greeting Message</label>
              <p className="text-xs text-muted-foreground">
                First message sent when a user starts a conversation
              </p>
              <Textarea
                value={form.greeting_message}
                onChange={(e) => setForm(prev => ({ ...prev, greeting_message: e.target.value }))}
                placeholder="Hello! How can I help you today?"
                rows={4}
                data-testid="greeting-message-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">System Prompt</label>
              <p className="text-xs text-muted-foreground">
                Instructions for the AI on how to behave
              </p>
              <Textarea
                value={form.system_prompt}
                onChange={(e) => setForm(prev => ({ ...prev, system_prompt: e.target.value }))}
                placeholder="You are a helpful support assistant..."
                rows={8}
                className="font-mono text-sm"
                data-testid="system-prompt-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* AiSensy Integration */}
        <Card className="bg-card/50 border-border/60">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${aiSensyStatus?.configured ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                  <Key className={`w-5 h-5 ${aiSensyStatus?.configured ? 'text-emerald-500' : 'text-amber-500'}`} />
                </div>
                <div>
                  <CardTitle className="text-lg">AiSensy Integration</CardTitle>
                  <CardDescription>Connect to WhatsApp via AiSensy API</CardDescription>
                </div>
              </div>
              {aiSensyStatus && (
                <Badge className={aiSensyStatus.connected 
                  ? "bg-emerald-500/10 text-emerald-500" 
                  : aiSensyStatus.configured 
                    ? "bg-amber-500/10 text-amber-500"
                    : "bg-red-500/10 text-red-500"
                }>
                  {aiSensyStatus.connected ? (
                    <><CheckCircle2 className="w-3 h-3 mr-1" /> Ready</>
                  ) : aiSensyStatus.configured ? (
                    <><AlertCircle className="w-3 h-3 mr-1" /> Campaign Needed</>
                  ) : (
                    <><AlertCircle className="w-3 h-3 mr-1" /> Not Configured</>
                  )}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status Banner */}
            {aiSensyStatus?.connected ? (
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-emerald-500">AiSensy Fully Configured</p>
                    <p className="text-muted-foreground mt-1">
                      API credentials and campaign are configured. The bot is ready to automatically reply to WhatsApp messages.
                    </p>
                    <div className="text-xs text-muted-foreground mt-2 space-y-1">
                      {aiSensyStatus.api_key_preview && (
                        <p className="font-mono">API Key: {aiSensyStatus.api_key_preview} ({aiSensyStatus.api_key_length} chars)</p>
                      )}
                      {aiSensyStatus.campaign_name && (
                        <p className="font-mono">Campaign: {aiSensyStatus.campaign_name}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : aiSensyStatus?.warning ? (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-red-500">API Key Issue Detected</p>
                    <p className="text-muted-foreground mt-1">
                      {aiSensyStatus.warning}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2 font-mono">
                      Current key: {aiSensyStatus.api_key_preview} ({aiSensyStatus.api_key_length} chars)
                    </p>
                  </div>
                </div>
              </div>
            ) : aiSensyStatus?.configured ? (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-500">API Campaign Required</p>
                    <p className="text-muted-foreground mt-1">
                      API key is configured but you need to set up an <strong>API Campaign</strong> in AiSensy for the bot to send replies.
                      WhatsApp requires using approved templates to send messages.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-red-500">Configuration Required</p>
                    <p className="text-muted-foreground mt-1">
                      You need to configure your AiSensy API credentials to enable WhatsApp messaging.
                      Get your API key from your AiSensy dashboard.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">AiSensy API Key</label>
              <p className="text-xs text-muted-foreground">
                Get this from AiSensy Dashboard → <strong>Manage</strong> → <strong>API Key</strong> (typically 32+ characters)
              </p>
              <div className="relative">
                <Input
                  type={showApiKey ? "text" : "password"}
                  value={form.aisensy_api_key}
                  onChange={(e) => setForm(prev => ({ ...prev, aisensy_api_key: e.target.value }))}
                  placeholder="Enter your AiSensy API key"
                  className="pr-10"
                  data-testid="aisensy-api-key-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              {form.aisensy_api_key && form.aisensy_api_key.length < 30 && (
                <p className="text-xs text-amber-500">
                  ⚠️ API key seems short ({form.aisensy_api_key.length} chars). AiSensy keys are usually 32+ characters. Please verify you copied the complete key from Manage → API Key.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Project ID</label>
              <p className="text-xs text-muted-foreground">
                Optional - Found in your AiSensy project settings
              </p>
              <Input
                value={form.aisensy_project_id}
                onChange={(e) => setForm(prev => ({ ...prev, aisensy_project_id: e.target.value }))}
                placeholder="Your AiSensy project ID"
                data-testid="aisensy-project-id-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">API Campaign Name <span className="text-red-500">*</span></label>
              <p className="text-xs text-muted-foreground">
                Required. Create a template with <strong>only {'{{1}}'} as the body</strong> for natural AI responses.
              </p>
              <Input
                value={form.aisensy_campaign_name}
                onChange={(e) => setForm(prev => ({ ...prev, aisensy_campaign_name: e.target.value }))}
                placeholder="e.g., monk_ea_bot_reply"
                data-testid="aisensy-campaign-name-input"
              />
              {aiSensyStatus?.configured && !aiSensyStatus?.campaign_configured && (
                <p className="text-xs text-amber-500 mt-1">
                  Campaign name is required for the bot to send replies
                </p>
              )}
            </div>

            {/* Natural Response Tips */}
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm font-medium text-primary mb-2">For Natural AI Responses:</p>
              <p className="text-xs text-muted-foreground mb-2">
                Create a WhatsApp template with <strong>ONLY {'{{1}}'}</strong> as the entire body text. This allows the AI response to appear as natural free-form text.
              </p>
              <div className="bg-muted/30 p-2 rounded text-xs font-mono">
                Template Body: {'{{1}}'}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                The AI response will replace {'{{1}}'} completely, making it look like a human reply.
              </p>
            </div>

            {/* Campaign Setup Instructions */}
            {aiSensyStatus?.configured && !aiSensyStatus?.campaign_configured && (
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <p className="text-sm font-medium text-blue-400 mb-2">How to Create API Campaign:</p>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Go to AiSensy → Campaigns → +Launch → API Campaign</li>
                  <li>Create a new template with body: <code className="bg-muted px-1 rounded">{'{{1}}'}</code></li>
                  <li>Wait for WhatsApp approval (usually a few hours)</li>
                  <li>Create API Campaign using that template</li>
                  <li>Set the campaign to "Live"</li>
                  <li>Enter the campaign name above and save</li>
                </ol>
              </div>
            )}

            {/* Test Connection */}
            {aiSensyStatus?.connected && (
              <div className="pt-4 border-t border-border/50 space-y-3">
                <label className="text-sm font-medium">Test WhatsApp Connection</label>
                <p className="text-xs text-muted-foreground">
                  Send a test message to verify your WhatsApp integration is working
                </p>
                <div className="flex gap-2">
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="Phone number (e.g., 919876543210)"
                    className="flex-1"
                    data-testid="test-phone-input"
                  />
                  <Button
                    variant="secondary"
                    onClick={handleTestConnection}
                    disabled={testingConnection || !testPhone.trim()}
                    data-testid="test-connection-btn"
                  >
                    {testingConnection ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      'Send Test'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Webhook Information */}
        <Card className="bg-card/50 border-border/60">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Settings className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Webhook Configuration</CardTitle>
                <CardDescription>Use this URL in your AiSensy webhook settings</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Webhook URL</label>
                <div className="flex gap-2">
                  <Input
                    value={`${process.env.REACT_APP_BACKEND_URL}/api/webhook/aisensy`}
                    readOnly
                    className="font-mono text-sm bg-muted/30"
                    data-testid="webhook-url"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/aisensy`);
                      toast.success('Webhook URL copied to clipboard');
                    }}
                    data-testid="copy-webhook-url"
                  >
                    Copy
                  </Button>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-muted/30 space-y-2">
                <p className="text-sm font-medium">Setup Instructions:</p>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Log into your AiSensy dashboard</li>
                  <li>Go to Settings &rarr; Webhooks</li>
                  <li>Add a new webhook with the URL above</li>
                  <li>Enable incoming message notifications</li>
                  <li>Save and test the webhook</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex items-center justify-between pt-4">
          <Button 
            variant="outline" 
            onClick={loadSettings}
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
